import { loadEcsTreatments } from '@ms/yammer-data/dist/domains/ecs/actions';
import { addEtagToAnalyticsEvent } from '@ms/yammer-data/dist/domains/ecs/addEtagToAnalyticsEvent';
import { addEtagToAnalyticsV2Event } from '@ms/yammer-data/dist/domains/ecs/addEtagToAnalyticsV2Event';
import { convertLegacyId } from '@ms/yammer-data/dist/domains/legacyId/actions';
import { isIdConversionRequested } from '@ms/yammer-data/dist/domains/legacyId/selectors';
import { setNetworkContext } from '@ms/yammer-data/dist/domains/networkContext/actions';
import { setPersistKey } from '@ms/yammer-data/dist/domains/persistConfig/actions';
import { getRawUserLocaleSetting } from '@ms/yammer-data/dist/domains/session/selectors';
import { configure } from '@ms/yammer-data/dist/domains/sessionConfigure/actions';
import { loadCurrentUser } from '@ms/yammer-data/dist/domains/sessionLoadCurrentUser/actions';
import { setTelemetryUserContext } from '@ms/yammer-data/dist/domains/telemetry/actions';
import { ErrorCode, TelemetryMiddleware, addMiddleware } from '@ms/yammer-data/dist/telemetry';
import { ConvertibleObjectType } from '@ms/yammer-graphql';
import { setLocale } from '@ms/yammer-i18n-react';
import { PopupAuthenticator } from '@ms/yammer-web-authenticators';
import { writeHostingCookies } from '@ms/yammer-web-support/dist/hosting';
import { getNetworkContext, getQueryParameter } from '@ms/yammer-web-support/dist/location';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { YammerEmbedState } from '../../../state/types';
import config from '../../config';
import { persistConfig } from '../../persistedState';

import { reportErrorWithCode } from './bootstrapTelemetry';

export type ActionDispatcher = (
  dispatch: ThunkDispatch<YammerEmbedState, Record<string, unknown>, AnyAction>
) => Promise<void>;

export const loadUserData: ActionDispatcher = async (dispatch) => {
  try {
    await dispatch(loadCurrentUser());
    await dispatch(setTelemetryUserContext());
  } catch (err) {
    reportErrorWithCode(err, ErrorCode.LoadCurrentUserFailed);
    throw err;
  }
};

type SetupFeatureFlighting = (
  dispatch: ThunkDispatch<YammerEmbedState, Record<string, unknown>, AnyAction>,
  getState: () => YammerEmbedState
) => Promise<void>;
export const setupFeatureFlighting: SetupFeatureFlighting = async (dispatch) => {
  try {
    await dispatch(loadEcsTreatments({ persistKey: persistConfig.key }));
  } catch (err) {
    reportErrorWithCode(err, ErrorCode.InitializeFeatureFlightingFailed);
  }
};

export const ensureTokensAcquired: (authenticator: PopupAuthenticator) => Promise<void> = async (authenticator) => {
  const yammerToken = await authenticator.acquireToken([config.adal.resources.yammer]);
  try {
    writeHostingCookies({ yammerToken, shouldSetSameSite: true });
  } catch (error) {
    reportErrorWithCode(error, ErrorCode.WriteHostingCookiesFailed);
    throw error;
  }
};

export const configureSession: (
  dispatch: ThunkDispatch<YammerEmbedState, Record<string, unknown>, AnyAction>
) => void = (dispatch) => {
  dispatch(configure(config));
};

export const configureNetworkContext: (
  dispatch: ThunkDispatch<YammerEmbedState, Record<string, unknown>, AnyAction>
) => void = (dispatch) => {
  const networkContext = getNetworkContext(window.location.pathname);
  dispatch(setNetworkContext(networkContext));
};

export const savePersistKey: (dispatch: ThunkDispatch<YammerEmbedState, Record<string, unknown>, AnyAction>) => void = (
  dispatch
) => {
  dispatch(setPersistKey(persistConfig.key));
};

type GetAddEtagMiddleware = (getState: () => YammerEmbedState) => TelemetryMiddleware;
const getAddEtagToAnalyticsEventMiddleware: GetAddEtagMiddleware = (getState) => (event) =>
  addEtagToAnalyticsEvent(event, getState);

type GetAddV2EtagMiddleware = (getState: () => YammerEmbedState) => TelemetryMiddleware;
const getAddV2EtagToAnalyticsEventMiddleware: GetAddV2EtagMiddleware = (getState) => (event) =>
  addEtagToAnalyticsV2Event(event, getState);

export const addTelemetryMiddleware: (getState: () => YammerEmbedState) => void = (getState) => {
  const etagMiddleware = getAddEtagToAnalyticsEventMiddleware(getState);
  const v2EtagMiddleware = getAddV2EtagToAnalyticsEventMiddleware(getState);
  addMiddleware(etagMiddleware);
  addMiddleware(v2EtagMiddleware);
};

export const setLocaleFromUserData: (
  dispatch: ThunkDispatch<YammerEmbedState, Record<string, unknown>, AnyAction>,
  state: YammerEmbedState
) => void = (dispatch, state) => {
  const defaultLocale = 'en-US';
  const currentLocale = getRawUserLocaleSetting(state) || defaultLocale;
  dispatch(setLocale(currentLocale));
};

type ConvertDefaultGroupLegacyId = (
  dispatch: ThunkDispatch<YammerEmbedState, Record<string, unknown>, AnyAction>,
  state: YammerEmbedState
) => void;
export const convertDefaultGroupLegacyId: ConvertDefaultGroupLegacyId = (dispatch, state) => {
  const defaultGroupId = getQueryParameter('defaultGroupId') as string;
  if (!defaultGroupId) {
    return;
  }

  if (!isIdConversionRequested(state, defaultGroupId, ConvertibleObjectType.GROUP)) {
    dispatch(convertLegacyId(defaultGroupId, ConvertibleObjectType.GROUP));
  }
};
